/**
 * VCloud template actions
 */
import type { DataTableAction } from '@/components/ui/data-table-customized'
import { TFunction } from 'i18next'
import { ServiceStatus } from '@/modules/Service/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import CancelIcons from '@/icons/CancelIcons'
import ConfirmServiceIcons from '@/icons/ConfirmServiceIcons'
import { confirmService, rejectedService } from '@/modules/Service/services'
import type { VCloudRow } from './vcloud-types'

export const useVCloudActions = (
    t: TFunction
): DataTableAction<VCloudRow>[] => {
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    const { mutateAsync: confirmMutate } = useMutationWithHandler(
        { mutationFn: confirmService },
        {
            successMessage: t('confirm-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICE,
        }
    )

    const { mutateAsync: rejectMutate } = useMutationWithHandler(
        { mutationFn: rejectedService },
        {
            successMessage: t('rejected-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
        }
    )

    // Only show actions on service-header rows
    const isSubRow = (row: VCloudRow) => row._rowType !== 'service-header'

    return [
        {
            key: 'confirm',
            label: t('confirm-service'),
            icon: <ConfirmServiceIcons />,
            variant: 'success',
            onClick: (row) => {
                if (!row.service_id) return
                setConfirmation({
                    open: true,
                    title: t('confirm-service'),
                    description: t('confirm-service-warning'),
                    onConfirm: () => confirmMutate(row.service_id!),
                })
            },
            disabled: (row) =>
                row.service_status !== ServiceStatus.WaitingConfirm,
            hidden: isSubRow,
        },
        {
            key: 'reject',
            label: t('service-rejected'),
            icon: <CancelIcons />,
            variant: 'danger',
            onClick: (row) => {
                if (!row.service_id) return
                setConfirmation({
                    open: true,
                    useReason: true,
                    title: t('rejected-service'),
                    description: t('rejected-service-warning'),
                    onConfirm: (reason) =>
                        rejectMutate({
                            serviceID: row.service_id!,
                            reason: reason as string,
                        }),
                })
            },
            disabled: (row) =>
                row.service_status !== ServiceStatus.WaitingConfirm,
            hidden: isSubRow,
        },
        {
            key: 'cancel',
            label: t('cancel-service'),
            icon: <CancelIcons />,
            variant: 'danger',
            onClick: (row) =>
                setModal('ServiceModals/CancelledServiceModal/index', {
                    service: row,
                }),
            disabled: (row) => row.service_status !== ServiceStatus.Active,
            hidden: isSubRow,
        },
    ]
}
